import {
  base64ToUint8Array, decryptCBC, encryptCBC, pkcs7Pad, pkcs7Strip, uint8ArrayToBase64, uint8ArrayToUtf8,
  utf8ToUint8Array,
} from "./aes";
import { SDKStatusCode, SDKStorageKey } from "./enum";
import md5 from "./md5";
import type { SDKEnv, SDKPlatform } from "./enum";
import type {
  SDKRuntime, SDKLogArgs, SDKLogResult, SDKOptions, SDKQueue, SDKRequest, SDKRequestOptions, SDKResponseOptions,
  SDKResult, SDKState, SDKStorage, SDKTrackArgs, SDKTrackResult, SDKUserInfo, SDKValidateArgs, SDKValidateResult,
  SDKVersion, SDKPlugin, SDK, SDKPluginObjectInstall, SDKPluginArrayInstall, SDKController, SDKGlobalOptions,
} from "./type";

// SDK核心实现
export abstract class SDKService implements SDKController {
  // 构造函数
  public constructor() {
    setTimeout(() => {
      void (async () => {
        try {
          await Promise.all([this.notify(), this.report()]);
        } catch (error) {
          this.trackList = [];
          clearTimeout(this.state.notifyTimer);
          clearTimeout(this.state.reportTimer);
          this.state.notifyTimer = -1;
          this.state.reportTimer = -1;
          this.state.reportSend = false;
          this.log({ code: SDKStatusCode.Error, msg: "任务调度失败", data: error });
        }
      })();
    }, 1000);
  }

  // 版本号
  public get version(): SDKVersion {
    return import.meta.env.VITE_SDK_VERSION;
  }

  // 运行环境
  public get runtime(): SDKRuntime {
    return import.meta.env.MODE as SDKRuntime;
  }

  // 内部参数
  protected readonly state: SDKState = {
    debug: false,
    options: null,
    plugins: {},
    notifyTimer: -1,
    reportTimer: -1,
    reportSend: false,
    trackTimer: -1,
    trackQueue: [],
  };

  // 调试模式
  public get debug(): boolean {
    return this.state.debug;
  }
  // 调试模式
  public set debug(value: boolean) {
    if (value) {
      console.warn(
        [
          "调试信息：",
          `当前接入版本：${this.version}，有问题可先尝试更新最新版SDK`,
          "调试模式已开启，可以通过控制台错误信息处理，无法解决请联系技术支持",
        ].join("\n"),
      );
    }
    this.state.debug = value;
  }

  // 系统参数
  public get options(): SDKOptions | SDKGlobalOptions | null {
    return this.state.options;
  }
  // 系统参数
  public set options(options: SDKOptions | SDKGlobalOptions | null) {
    this.state.options = options;
  }

  // 内部插件
  public get plugins(): Record<string, unknown> {
    return this.state.plugins;
  }

  // 设备标识
  public get deviceId(): string {
    const deviceId = this.localStorage.getItem(SDKStorageKey.DeviceId);
    if (deviceId) return deviceId as string;
    const value = `${this.version}${this.generateRandomValues().join("")}${Date.now()}`;
    const hash = this.calculateMD5Hex(value);
    this.localStorage.setItem(SDKStorageKey.DeviceId, hash);
    return hash;
  }

  // 通知队列
  private get notifyList(): SDKQueue {
    const queue = this.localStorage.getItem(SDKStorageKey.NotifyQueue);
    return queue ? (queue as SDKQueue) : [];
  }

  // 通知队列
  private set notifyList(value: SDKQueue) {
    this.localStorage.setItem(SDKStorageKey.NotifyQueue, value);
  }

  // 日志队列
  private get trackList(): SDKQueue {
    const queue = this.localStorage.getItem(SDKStorageKey.TrackQueue);
    return queue ? (queue as SDKQueue) : [];
  }

  // 日志队列
  private set trackList(value: SDKQueue) {
    this.localStorage.setItem(SDKStorageKey.TrackQueue, value);
  }

  // 用户信息
  public get userInfo(): SDKUserInfo | null {
    const userInfo = this.localStorage.getItem(SDKStorageKey.UserInfo);
    return userInfo ? (userInfo as SDKUserInfo) : null;
  }

  // 用户信息
  public set userInfo(value: SDKUserInfo | null) {
    this.localStorage.setItem(SDKStorageKey.UserInfo, value);
  }
  // 游戏平台OpenId
  public get openId(): string {
    return this.userInfo?.openId ?? "";
  }

  // 游戏平台UnionId
  public get unionId(): string {
    return this.userInfo?.unionId ?? "";
  }

  // 账号凭证Token
  public get token(): string {
    return this.userInfo?.token ?? "";
  }

  // 对象转查询字符串
  public stringifyJson(json: Record<string, unknown>, options?: { encode: boolean }): string {
    const stringifyList = [];
    for (const key in json) {
      const value = json[key] as null | undefined | string | number | boolean;
      if (value === null || value === undefined) continue;
      const encodeValue = options?.encode ? encodeURIComponent(value) : value;
      stringifyList.push(`${key}=${encodeValue}`);
    }
    return stringifyList.join("&");
  }

  // 查询字符串转对象
  public parseJson(query: string): Record<string, string> {
    if (!query) return {};
    const queryList = query.split("&");
    const json: Record<string, string> = {};
    for (const queryItem of queryList) {
      const stringList = queryItem.split("=");
      json[stringList[0]] = stringList.slice(1).join("=");
    }
    return json;
  }

  // 格式化时间
  public formatDateTime(date: Date | number): string {
    const dateValue: Date = typeof date === "number" ? new Date(date) : date;
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, "0");
    const day = String(dateValue.getDate()).padStart(2, "0");
    const hours = String(dateValue.getHours()).padStart(2, "0");
    const minutes = String(dateValue.getMinutes()).padStart(2, "0");
    const seconds = String(dateValue.getSeconds()).padStart(2, "0");
    const milliseconds = String(dateValue.getMilliseconds()).padStart(3, "0");
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
  }

  // 生成随机字符串
  public generateRandomValues(length = 8): Array<string> {
    const charCodeList = "ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz2345678";
    const characterValueList = [];
    for (let i = 0; i < length; i++) {
      const character = charCodeList.charAt(Math.floor(Math.random() * charCodeList.length));
      characterValueList.push(character);
    }
    return characterValueList;
  }

  // 参数签名
  public signature(args: Record<string, unknown>, key: string): Record<string, string> {
    const signValues: Record<string, string> = {
      ...args,
      sign_type: "md5",
      sign_nonce: this.generateRandomValues().join(""),
      sign_version: "1.0",
    };
    const signKeys = Object.keys(signValues).sort();
    const signValueList = Array.from(signKeys, (singKey) => {
      const value = signValues[singKey];
      if (value === null || value === undefined) return null;
      return `${singKey}=${typeof value === "object" ? JSON.stringify(value) : value}`;
    });
    const signStringValue = signValueList.filter((v) => v !== null).join("&");
    let signStringCode = encodeURIComponent(signStringValue);
    signStringCode = signStringCode.replace(/!/gu, "%21");
    signStringCode = signStringCode.replace(/\*/gu, "%2A");
    signStringCode = signStringCode.replace(/'/gu, "%27");
    signStringCode = signStringCode.replace(/\(/gu, "%28");
    signStringCode = signStringCode.replace(/\)/gu, "%29");
    signValues.sign = this.calculateMD5Hex(`${signStringCode}&${key}`);
    return signValues;
  }

  // MD5加密
  public calculateMD5Hex(value: string): string {
    return md5(value);
  }

  // AES加密
  public calculateAESEncrypt(args: { key: string; iv: string; plaintext: string }): string {
    const key = utf8ToUint8Array(args.key);
    const iv = utf8ToUint8Array(args.iv);
    const plaintext = utf8ToUint8Array(args.plaintext);
    return uint8ArrayToBase64(encryptCBC(key, iv, pkcs7Pad(plaintext)));
  }

  // AES解密
  public calculateAESDecrypt(args: { key: string; iv: string; ciphertext: string }): string {
    const key = utf8ToUint8Array(args.key);
    const iv = utf8ToUint8Array(args.iv);
    const ciphertext = base64ToUint8Array(args.ciphertext);
    return uint8ArrayToUtf8(pkcs7Strip(decryptCBC(key, iv, ciphertext)));
  }

  // 数据加密
  public encrypt(args: { key: string; plaintext: string }): string {
    const encryptText = this.calculateAESEncrypt({ ...args, iv: "0000000000000000" });
    return encryptText
      .replace(/\+/gu, "-")
      .replace(/\//gu, "_")
      .replace(/[=]/gu, "")
      .replace(/\n/gu, "");
  }

  // 数据解密
  public decrypt(args: { key: string; ciphertext: string }): string {
    let ciphertext = args.ciphertext.replace(/-/gu, "+").replace(/_/gu, "/");
    ciphertext += new Array(4 - (ciphertext.length % 4)).fill("=").join("");
    return this.calculateAESDecrypt({ ...args, ciphertext, iv: "0000000000000000" });
  }

  // 通用请求
  public async request<T>(args: SDKRequestOptions): Promise<SDKResponseOptions<T>> {
    if (args.method === "GET") {
      return await this.httpRequest.get<T>(args);
    }
    if (args.method === "POST") {
      return await this.httpRequest.post<T>(args);
    }
    if (args.method === "PUT") {
      return await this.httpRequest.put<T>(args);
    }
    if (args.method === "DELETE") {
      return await this.httpRequest.delete<T>(args);
    }
    throw new Error("request method not support");
  }

  // 聚合网关请求
  public abstract requestGateway<T>(args: SDKRequestOptions): Promise<SDKResponseOptions<T>>;

  // 通行证请求
  public abstract requestPassport<T>(args: SDKRequestOptions): Promise<SDKResponseOptions<T>>;

  // 聚合服务请求
  public abstract requestCombine<T>(args: SDKRequestOptions): Promise<SDKResponseOptions<T>>;

  // 客服请求
  public abstract requestService<T>(args: SDKRequestOptions): Promise<SDKResponseOptions<T>>;

  // 日志上报请求
  public abstract requestTrack<T>(args: SDKRequestOptions): Promise<SDKResponseOptions<T>>;

  // 通知回调请求
  public async requestNotify(args: SDKRequestOptions): Promise<SDKResponseOptions<null>> {
    const value = JSON.stringify(args);
    const key = this.calculateMD5Hex(value);
    this.notifyList = [...this.notifyList, { key, value }];
    await this.notify();
    return { status: "success", code: SDKStatusCode.Success, msg: "success", data: null };
  }

  // 通知服务端
  public async notify(count = 1): Promise<void> {
    if (!this.support) return;
    if (count === 1) {
      clearTimeout(this.state.notifyTimer);
    }
    if (this.notifyList.length === 0) return;

    const keys: Array<string | null> = [];
    for (const notifyItem of this.notifyList) {
      try {
        const options = JSON.parse(notifyItem.value) as SDKRequestOptions;
        const { status } = await this.request(options);
        keys.push(status === "success" ? notifyItem.key : null);
      } catch (error) {
        this.log({ code: SDKStatusCode.Error, msg: "服务端回调通知失败", data: error });
      }
    }

    this.notifyList = this.notifyList.filter(({ key }) => !keys.includes(key));
    if (this.notifyList.length === 0) return;
    this.state.notifyTimer = setTimeout(() => {
      if (count >= 10) {
        this.notifyList = [];
        return;
      }
      void this.notify(count + 1);
    }, count * 1000) as unknown as number;
  }

  // 日志上报
  public async report(): Promise<SDKResult<null>> {
    if (!this.support) {
      return { status: "success", code: SDKStatusCode.Success, msg: "success", data: null };
    }
    if (this.state.reportSend || this.trackList.length === 0) {
      clearTimeout(this.state.reportTimer);
      this.state.reportTimer = -1;
      return { status: "success", code: SDKStatusCode.Success, msg: "success", data: null };
    }
    const dataList = this.trackList.slice(0, 20);
    const keys = Array.from(dataList, ({ key }) => key);
    const values = Array.from(dataList, ({ value }) => {
      return JSON.parse(value) as Record<string, unknown>;
    });

    const { app_id, app_channel, app_version } = values[0] as Record<string, string>;
    const source = [app_id, app_channel, app_version].filter((v) => v).join("-");

    this.state.reportSend = true;
    const { status } = await this.requestTrack({
      url: "/logstores/web_track/track",
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-log-apiversion": "0.6.0",
        "x-log-bodyrawsize": "0",
      },
      data: { __topic__: app_id, __source__: source, __logs__: values, __tags__: {} },
    });
    this.state.reportSend = false;
    if (status === "success") {
      this.trackList = this.trackList.filter(({ key }) => !keys.includes(key));
    }

    if (this.trackList.length > 0) {
      clearTimeout(this.state.reportTimer);
      this.state.reportTimer = setTimeout(() => {
        void this.report();
      }, 3000) as unknown as number;
    } else {
      this.state.reportTimer = -1;
    }

    if (status === "error") {
      return { status: "error", code: SDKStatusCode.Error, msg: "error", data: null };
    }
    return { status: "success", code: SDKStatusCode.Success, msg: "success", data: null };
  }

  // 日志追踪
  public async track(args: SDKTrackArgs): Promise<SDKTrackResult> {
    this.state.trackQueue.push({
      key: `${Date.now()}${this.trackList.length}`,
      value: JSON.stringify({
        app_id: String(args.appId),
        app_version: args.appVersion,
        app_channel: args.appChannel,
        user_id: args.userId,
        user_tag: args.userTag,
        device_id: args.deviceId,
        device_model: args.deviceModel,
        os_type: args.osType,
        os_version: args.osVersion,
        network_type: args.networkType,
        event_id: args.eventId,
        event_name: args.eventName,
        event_group: args.eventGroup,
        event_value: JSON.stringify(args.eventValue),
        event_time: args.eventTime,
        event_level: args.eventLevel,
      }),
    });

    if (this.state.trackTimer !== -1) {
      return { status: "success", code: SDKStatusCode.Success, msg: "success", data: null };
    }
    await new Promise((resolve) => {
      this.state.trackTimer = setTimeout(resolve, 100) as unknown as number;
    });
    clearTimeout(this.state.trackTimer);
    this.state.trackTimer = -1;

    this.trackList = [...this.trackList, ...this.state.trackQueue];
    this.state.trackQueue = [];

    if (this.state.reportTimer !== -1) {
      return { status: "success", code: SDKStatusCode.Success, msg: "success", data: null };
    }
    try {
      return await this.report();
    } catch (error) {
      clearTimeout(this.state.reportTimer);
      this.state.reportTimer = -1;
      this.state.reportSend = false;
      this.trackList = [];
      this.log({ code: SDKStatusCode.Error, msg: "日志上报失败", data: error });
      return { status: "error", code: SDKStatusCode.Error, msg: "error", data: null };
    }
  }

  // 前置校验
  public async validate(args: SDKValidateArgs): Promise<SDKValidateResult> {
    if (args.includes("support") && !this.support) {
      const msg = "当前环境无法运行NebulaSDK";
      return { status: "error", code: SDKStatusCode.NoSupport, msg, data: null };
    }
    if (args.includes("init") && !this.options) {
      const msg = "请先调用init方法进行初始化";
      return { status: "error", code: SDKStatusCode.NoInit, msg, data: null };
    }
    if (args.includes("login") && !this.userInfo) {
      const msg = "请先调用login方法进行登录";
      return { status: "error", code: SDKStatusCode.NoLogin, msg, data: null };
    }
    const result = { status: "success", code: SDKStatusCode.Success, msg: "成功", data: null };
    return Promise.resolve(result as SDKValidateResult);
  }

  // 打印日志
  public log(args: SDKLogArgs): SDKLogResult {
    console.warn(`NebulaSDK v${this.version}: ${args.code} - ${args.msg}`);
    if (args.data) {
      console.warn(JSON.stringify(args.data));
    }
    return { status: "success", code: SDKStatusCode.Success, msg: "success", data: null };
  }

  // 重试运行
  public async retry<T>(
    func: () => Promise<T> | T,
    final?: ((error: unknown) => Promise<T> | T) | null,
    count = 5,
  ): Promise<T> {
    const retryFunc = async (retryCount: number): Promise<T> => {
      try {
        return await func();
      } catch (error) {
        if (retryCount > count) {
          if (final) {
            return await final(error);
          }
          throw error;
        }
        return await retryFunc(retryCount + 1);
      }
    };
    return await retryFunc(1);
  }

  // 转换请求结果
  public async transformRequestCode(args: {
    type: "account" | "payment";
    code: number | string;
  }): Promise<SDKStatusCode | undefined> {
    const error: Record<string, Record<string, SDKStatusCode>> = {
      account: {
        2100607: SDKStatusCode.LoginFreezeError,
      },
      payment: {
        2120143: SDKStatusCode.AccountExceptionError,
        2120144: SDKStatusCode.DeviceExceptionError,
        2120147: SDKStatusCode.CreateExceptionError,
        2120148: SDKStatusCode.PayAccountFreezeError,
      },
    };
    return await Promise.resolve(error[args.type][args.code]);
  }

  // 创建失败结果
  public async createErrorResult<V, T>(args: V, data: T): Promise<SDKResult<T>> {
    this.log({ code: SDKStatusCode.Error, msg: "暂不支持该接口", data: args });
    const result = { status: "error", code: SDKStatusCode.Error, msg: "暂不支持该接口", data };
    return await Promise.resolve(result as SDKResult<T>);
  }

  // 创建成功结果
  public async createSuccessResult<V, T>(args: V, data: T): Promise<SDKResult<T>> {
    this.log({ code: SDKStatusCode.Success, msg: "成功", data: args });
    const result = { status: "success", code: SDKStatusCode.Success, msg: "成功", data };
    return await Promise.resolve(result as SDKResult<T>);
  }

  // 安装插件
  public install<T extends Array<unknown>>(
    instance: SDK,
    plugin: SDKPlugin<T>,
    ...options: T
  ): void;
  public install<T>(instance: SDK, plugin: SDKPlugin<T>, options: T): void;
  public install(instance: SDK, plugin: SDKPlugin, ...options: Array<unknown>): void {
    if (options.length === 1 && !Array.isArray(options[0])) {
      const install = plugin.install as SDKPluginObjectInstall<unknown>;
      install.call(this, instance, options[0]);
    } else {
      const install = plugin.install as SDKPluginArrayInstall<Array<unknown>>;
      install.apply(this, [instance, ...options]);
    }
  }

  // 环境支持
  public abstract get support(): boolean;

  // 渠道标识
  public abstract get env(): SDKEnv;

  // 运行平台
  public abstract get platform(): SDKPlatform;

  // 本地存储
  protected abstract readonly localStorage: SDKStorage;

  // 请求管理
  protected abstract readonly httpRequest: SDKRequest;
}
